import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Calendar, ChevronDown, Clock, Building2 } from 'lucide-react'; // eslint-disable-line no-unused-vars
import { getPrayerTimesByDate } from '../api';

const PRAYERS = [
  { key: 'fajr', label: 'Fajr', icon: '🌙', color: 'from-indigo-900 to-navy-900', accent: 'text-indigo-400', border: 'border-indigo-500/30' },
  { key: 'dhuhr', label: 'Dhuhr', icon: '☀️', color: 'from-amber-900/50 to-navy-900', accent: 'text-amber-400', border: 'border-amber-500/30' },
  { key: 'asr', label: 'Asr', icon: '🌤', color: 'from-orange-900/50 to-navy-900', accent: 'text-orange-400', border: 'border-orange-500/30' },
  { key: 'maghrib', label: 'Maghrib', icon: '🌇', color: 'from-pink-900/50 to-navy-900', accent: 'text-pink-400', border: 'border-pink-500/30' },
  { key: 'isha', label: 'Isha', icon: '🌃', color: 'from-purple-900/50 to-navy-900', accent: 'text-purple-400', border: 'border-purple-500/30' },
];

function PrayerCard({ prayer, mosqueData }) {
  const [expanded, setExpanded] = useState(false);

  const timesAvailable = mosqueData.filter((m) => m[prayer.key]);
  const earliest = timesAvailable.length > 0 ? timesAvailable.map((m) => m[prayer.key]).sort()[0] : null;

  return (
    <motion.div
      layout
      className={`rounded-2xl overflow-hidden border ${prayer.border} transition-all duration-300 ${expanded ? 'shadow-lg' : ''}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full bg-gradient-to-r ${prayer.color} p-5 flex items-center justify-between text-left hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-4">
          <div className="text-3xl">{prayer.icon}</div>
          <div>
            <h3 className="text-white font-bold text-xl">{prayer.label}</h3>
            {earliest && (
              <p className="text-sm text-gray-300 mt-0.5">
                Earliest: <span className={`font-semibold ${prayer.accent}`}>{earliest}</span>
              </p>
            )}
            {!earliest && (
              <p className="text-sm text-gray-500 mt-0.5">No times available</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm ${prayer.accent}`}>
            {timesAvailable.length} mosque{timesAvailable.length !== 1 ? 's' : ''}
          </span>
          <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-navy-900/80 border-t border-white/5">
              {mosqueData.length === 0 ? (
                <div className="p-6 text-center text-gray-400">No data available for this date.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-dark">
                    <thead>
                      <tr>
                        <th>Mosque</th>
                        <th>Address</th>
                        <th className={prayer.accent}>{prayer.label} Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mosqueData.map((entry) => (
                        <tr key={entry._id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-emerald-500/50 flex-shrink-0" />
                              <span className="font-medium text-white">{entry.mosqueId?.name}</span>
                            </div>
                          </td>
                          <td className="text-gray-500 text-xs">{entry.mosqueId?.address}</td>
                          <td>
                            <span className={`font-semibold ${entry[prayer.key] ? prayer.accent : 'text-gray-600'}`}>
                              {entry[prayer.key] || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function PrayersByDate() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState(today);
  const [prayerData, setPrayerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTimes = (date) => {
    setLoading(true);
    setError(null);
    getPrayerTimesByDate(date)
      .then((res) => setPrayerData(res.data))
      .catch(() => setError('Failed to load prayer times for this date.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTimes(today);
  }, []);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    fetchTimes(e.target.value);
  };

  const displayDate = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 badge-emerald mb-4">
            <Clock className="w-4 h-4" />
            Prayer Times by Date
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Daily <span className="gradient-text">Salah Times</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Select a date to view prayer times across all Oldham mosques
          </p>
        </motion.div>

        {/* Date Picker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card-light rounded-2xl p-6 mb-8"
        >
          <label className="block text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Select Date
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="input-dark w-full sm:w-auto rounded-xl px-4 py-3 text-white font-medium cursor-pointer"
          />
          {displayDate && (
            <p className="mt-3 text-gray-400 text-sm">{displayDate}</p>
          )}
        </motion.div>

        {/* Prayer Cards */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl h-20 shimmer" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20 glass-card rounded-2xl">
            <p className="text-red-400">{error}</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {prayerData.length === 0 && (
              <div className="glass-card rounded-2xl flex flex-col items-center justify-center py-16 text-center">
                <Calendar className="w-16 h-16 text-gray-600 mb-4" />
                <h3 className="text-white font-semibold text-xl mb-2">No Times Available</h3>
                <p className="text-gray-400">No prayer times have been recorded for this date yet.</p>
              </div>
            )}
            {PRAYERS.map((prayer) => (
              <PrayerCard
                key={prayer.key}
                prayer={prayer}
                mosqueData={prayerData}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
